<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMatterStore } from '../../stores/matterStore';
import { useSessionStore } from '../../stores/sessionStore';
import type { DialogSession } from '../../stores/sessionStore';

type SessionGroup = 'today' | 'yesterday' | 'last7days' | 'older';

// A monotonically-increasing counter the parent bumps to ask us to open and
// focus the search box (e.g. from the collapsed nav rail). Watched below.
const props = defineProps<{ searchTrigger?: number }>();

const router = useRouter();
const { t } = useI18n();
const matterStore = useMatterStore();
const sessionStore = useSessionStore();
const showNewSessionPrompt = ref(false);

// Reveal-on-demand search over the Recents list. `searchOpen` toggles the
// full-width box; `searchQuery` filters sessions by name client-side.
const searchOpen = ref(false);
const searchQuery = ref('');
const searchInput = ref<HTMLInputElement | null>(null);
const newSessionName = ref('');
const contextMenuSessionId = ref<string | null>(null);
const contextMenuPosition = ref({ x: 0, y: 0 });
const showDeleteConfirm = ref(false);
const sessionToDelete = ref<string | null>(null);
const deleting = ref(false);
const showRenamePrompt = ref(false);
const sessionToRename = ref<string | null>(null);
const renameNewName = ref('');
const renaming = ref(false);
const toastMessage = ref('');
const showToast = ref(false);

const expandedGroups = ref<Record<SessionGroup, boolean>>({
    today: false,
    yesterday: false,
    last7days: false,
    older: false,
});

// Groups in chronological order (most recent first) so we can find the
// latest non-empty grouping to auto-expand on first render.
const GROUP_ORDER: SessionGroup[] = ['today', 'yesterday', 'last7days', 'older'];
const initialExpansionDone = ref(false);

const selectSession = (session: DialogSession) => {
    const matterId = matterStore.currentMatter?.RecordId;
    if (!matterId) return;
    sessionStore.selectSession(session);
    router.push({ name: 'session', params: { matterId, sessionId: session.RecordId } });
};

const showContextMenu = (event: MouseEvent, sessionId: string) => {
    event.stopPropagation();
    contextMenuSessionId.value = sessionId;
    contextMenuPosition.value = { x: event.clientX, y: event.clientY };
};

const closeContextMenu = () => {
    contextMenuSessionId.value = null;
};

const confirmDelete = (sessionId: string) => {
    sessionToDelete.value = sessionId;
    showDeleteConfirm.value = true;
    closeContextMenu();
};

const cancelDelete = () => {
    if (deleting.value) return;
    showDeleteConfirm.value = false;
    sessionToDelete.value = null;
};

const performDelete = async () => {
    if (!sessionToDelete.value || deleting.value) return;
    const deletedId = sessionToDelete.value;
    deleting.value = true;
    try {
        await sessionStore.deleteSession(deletedId);
        showDeleteConfirm.value = false;
        sessionToDelete.value = null;
        displayToast(t('sessions.toast.deleteSuccess'));

        // If the deleted session was the one in the URL, the store has
        // already nulled currentSession (UI shows empty state via the
        // App.vue cascade) but the URL still references the gone id.
        // Walk up to /matters/:m so the URL stays in sync.
        if (router.currentRoute.value.params.sessionId === deletedId) {
            const matterId = matterStore.currentMatter?.RecordId;
            if (matterId) router.push({ name: 'matter', params: { matterId } });
        }
    } catch {
        displayToast(t('sessions.toast.deleteFailed'));
    } finally {
        deleting.value = false;
    }
};

const startRename = (sessionId: string) => {
    const session = sessionStore.sessions.find((s) => s.RecordId === sessionId);
    sessionToRename.value = sessionId;
    renameNewName.value = session ? session.Name : '';
    showRenamePrompt.value = true;
    closeContextMenu();
};

const cancelRename = () => {
    if (renaming.value) return;
    showRenamePrompt.value = false;
    sessionToRename.value = null;
    renameNewName.value = '';
};

const performRename = async () => {
    if (!renameNewName.value.trim() || !sessionToRename.value || renaming.value) {
        return;
    }
    renaming.value = true;
    try {
        await sessionStore.renameSession(sessionToRename.value, renameNewName.value.trim());
        showRenamePrompt.value = false;
        sessionToRename.value = null;
        renameNewName.value = '';
        displayToast(t('sessions.toast.renameSuccess'));
    } catch {
        displayToast(t('sessions.toast.renameFailed'));
    } finally {
        renaming.value = false;
    }
};

const displayToast = (message: string) => {
    toastMessage.value = message;
    showToast.value = true;
    setTimeout(() => {
        showToast.value = false;
    }, 3000);
};

const showNewChatPrompt = () => {
    const matterId = matterStore.currentMatter?.RecordId;
    if (!matterId) return;
    router.push({ name: 'matter', params: { matterId } });
};

const focusSearchInput = () => {
    nextTick(() => searchInput.value?.focus());
};

// Reveal the search box and give it focus. Used both by the in-list search
// icon and by the parent's collapsed-rail trigger.
const openSearch = () => {
    searchOpen.value = true;
    focusSearchInput();
};

const closeSearch = () => {
    searchOpen.value = false;
    searchQuery.value = '';
};

const toggleSearch = () => {
    if (searchOpen.value) {
        closeSearch();
    } else {
        openSearch();
    }
};

// Parent bumps `searchTrigger` (e.g. clicking search on the collapsed rail,
// which also expands the sidebar) to ask us to open and focus the box. The
// collapsed-rail path expands the sidebar in the same tick, which mounts this
// component *after* the bump — so we run immediately too, to catch a trigger
// value that was already set before we existed.
watch(
    () => props.searchTrigger,
    (val) => {
        if (val !== undefined && val > 0) openSearch();
    },
    { immediate: true },
);

const cancelNewSession = () => {
    showNewSessionPrompt.value = false;
    newSessionName.value = '';
};

const createNewSession = async () => {
    if (!newSessionName.value.trim()) {
        return;
    }

    const matterId = matterStore.currentMatter?.RecordId;
    if (!matterId) return;

    try {
        const newSession = await sessionStore.createSession(newSessionName.value.trim());
        showNewSessionPrompt.value = false;
        newSessionName.value = '';
        sessionStore.selectSession(newSession);
        router.push({ name: 'session', params: { matterId, sessionId: newSession.RecordId } });
    } catch {
        // Error handled in store
    }
};

const toggleGroup = (groupName: SessionGroup) => {
    expandedGroups.value[groupName] = !expandedGroups.value[groupName];
};

const normalizedQuery = computed(() => searchQuery.value.trim().toLowerCase());
const isSearching = computed(() => searchOpen.value && normalizedQuery.value.length > 0);

const groupedSessions = computed(() => {
    const all = sessionStore.sessions || [];
    const sessions = isSearching.value
        ? all.filter((s) => (s.Name || '').toLowerCase().includes(normalizedQuery.value))
        : all;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const last7Days = new Date(today);
    last7Days.setDate(last7Days.getDate() - 7);

    const groups: Record<SessionGroup, DialogSession[]> = {
        today: [],
        yesterday: [],
        last7days: [],
        older: [],
    };

    sessions.forEach((session) => {
        if (!session.LastInteractionTimestamp) {
            groups.older.push(session);
            return;
        }

        const sessionDate = new Date(session.LastInteractionTimestamp);
        const sessionDay = new Date(
            sessionDate.getFullYear(),
            sessionDate.getMonth(),
            sessionDate.getDate(),
        );

        if (sessionDay.getTime() === today.getTime()) {
            groups.today.push(session);
        } else if (sessionDay.getTime() === yesterday.getTime()) {
            groups.yesterday.push(session);
        } else if (sessionDay.getTime() >= last7Days.getTime()) {
            groups.last7days.push(session);
        } else {
            groups.older.push(session);
        }
    });

    const sortByTimestamp = (a: DialogSession, b: DialogSession) => {
        const timeA = a.LastInteractionTimestamp
            ? new Date(a.LastInteractionTimestamp).getTime()
            : 0;
        const timeB = b.LastInteractionTimestamp
            ? new Date(b.LastInteractionTimestamp).getTime()
            : 0;
        return timeB - timeA;
    };

    groups.today.sort(sortByTimestamp);
    groups.yesterday.sort(sortByTimestamp);
    groups.last7days.sort(sortByTimestamp);
    groups.older.sort(sortByTimestamp);

    return groups;
});

const hasAnySessions = computed(() =>
    GROUP_ORDER.some((group) => groupedSessions.value[group].length > 0),
);

// While searching we force every (non-empty, post-filter) group open so all
// matches are visible regardless of the user's manual expansion state.
const effectiveExpanded = computed<Record<SessionGroup, boolean>>(() => {
    if (isSearching.value) {
        return { today: true, yesterday: true, last7days: true, older: true };
    }
    return expandedGroups.value;
});

// On first load, expand the most recent grouping that actually has sessions
// so the user lands on something useful instead of a collapsed list.
watch(
    groupedSessions,
    (groups) => {
        if (initialExpansionDone.value) return;
        const latest = GROUP_ORDER.find((group) => groups[group].length > 0);
        if (!latest) return;
        expandedGroups.value[latest] = true;
        initialExpansionDone.value = true;
    },
    { immediate: true },
);

onMounted(async () => {
    // loadSessions is cached per matter in the store, so this is a no-op
    // when the list is already loaded (e.g. re-expanding the sidebar).
    try {
        await sessionStore.loadSessions();
    } catch {
        // Error handled in store
    }
});

void matterStore;
</script>

<template>
    <div class="dialog-session-list">
        <div class="sessions-actions">
            <div class="sessions-actions-row">
                <button
                    type="button"
                    class="sessions-action-btn"
                    :title="t('sessions.newDialogue')"
                    :aria-label="t('sessions.newDialogue')"
                    @click="showNewChatPrompt"
                >
                    <span class="material-icons">add</span>
                </button>
                <button
                    type="button"
                    class="sessions-action-btn"
                    :class="{ 'sessions-action-btn-active': searchOpen }"
                    :title="t('sessions.search')"
                    :aria-label="t('sessions.search')"
                    :aria-pressed="searchOpen"
                    @click="toggleSearch"
                >
                    <span class="material-icons">search</span>
                </button>
            </div>
            <div v-if="searchOpen" class="sessions-search">
                <span class="material-icons sessions-search-icon">search</span>
                <input
                    ref="searchInput"
                    v-model="searchQuery"
                    type="text"
                    class="sessions-search-input"
                    :placeholder="t('sessions.searchPlaceholder')"
                    @keyup.esc="closeSearch"
                />
                <button
                    v-if="searchQuery"
                    type="button"
                    class="sessions-search-clear"
                    :title="t('sessions.clearSearch')"
                    :aria-label="t('sessions.clearSearch')"
                    @click="searchQuery = ''"
                >
                    <span class="material-icons">close</span>
                </button>
            </div>
        </div>
        <div class="sessions-section">
            <h3 class="sessions-heading">{{ t('sessions.recents') }}</h3>

            <div v-if="sessionStore.loading" class="loading-small">{{ t('common.loading') }}</div>
            <div v-else-if="sessionStore.error" class="error-small">{{ sessionStore.error }}</div>

            <div v-else-if="!hasAnySessions" class="sessions-empty">
                {{ isSearching ? t('sessions.noMatches') : t('sessions.empty') }}
            </div>

            <div v-else class="sessions-list" @click="closeContextMenu">
                <div v-if="groupedSessions.today.length > 0" class="session-group">
                    <div class="session-group-header" @click.stop="toggleGroup('today')">
                        <span class="material-icons group-arrow">{{
                            effectiveExpanded.today ? 'expand_more' : 'chevron_right'
                        }}</span>
                        <span class="group-title">{{ t('sessions.groups.today') }}</span>
                    </div>
                    <div v-if="effectiveExpanded.today" class="session-group-content">
                        <div
                            v-for="session in groupedSessions.today"
                            :key="session.RecordId"
                            class="session-item"
                            :class="{
                                'session-item-active':
                                    sessionStore.currentSessionId === session.RecordId,
                            }"
                            @click="selectSession(session)"
                        >
                            <div class="session-content">
                                <div class="session-name">{{ session.Name }}</div>
                            </div>
                            <button
                                class="session-menu-button"
                                @click="showContextMenu($event, session.RecordId)"
                            >
                                ⋮
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="groupedSessions.yesterday.length > 0" class="session-group">
                    <div class="session-group-header" @click.stop="toggleGroup('yesterday')">
                        <span class="material-icons group-arrow">{{
                            effectiveExpanded.yesterday ? 'expand_more' : 'chevron_right'
                        }}</span>
                        <span class="group-title">{{ t('sessions.groups.yesterday') }}</span>
                    </div>
                    <div v-if="effectiveExpanded.yesterday" class="session-group-content">
                        <div
                            v-for="session in groupedSessions.yesterday"
                            :key="session.RecordId"
                            class="session-item"
                            :class="{
                                'session-item-active':
                                    sessionStore.currentSessionId === session.RecordId,
                            }"
                            @click="selectSession(session)"
                        >
                            <div class="session-content">
                                <div class="session-name">{{ session.Name }}</div>
                            </div>
                            <button
                                class="session-menu-button"
                                @click="showContextMenu($event, session.RecordId)"
                            >
                                ⋮
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="groupedSessions.last7days.length > 0" class="session-group">
                    <div class="session-group-header" @click.stop="toggleGroup('last7days')">
                        <span class="material-icons group-arrow">{{
                            effectiveExpanded.last7days ? 'expand_more' : 'chevron_right'
                        }}</span>
                        <span class="group-title">{{ t('sessions.groups.last7days') }}</span>
                    </div>
                    <div v-if="effectiveExpanded.last7days" class="session-group-content">
                        <div
                            v-for="session in groupedSessions.last7days"
                            :key="session.RecordId"
                            class="session-item"
                            :class="{
                                'session-item-active':
                                    sessionStore.currentSessionId === session.RecordId,
                            }"
                            @click="selectSession(session)"
                        >
                            <div class="session-content">
                                <div class="session-name">{{ session.Name }}</div>
                            </div>
                            <button
                                class="session-menu-button"
                                @click="showContextMenu($event, session.RecordId)"
                            >
                                ⋮
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="groupedSessions.older.length > 0" class="session-group">
                    <div class="session-group-header" @click.stop="toggleGroup('older')">
                        <span class="material-icons group-arrow">{{
                            effectiveExpanded.older ? 'expand_more' : 'chevron_right'
                        }}</span>
                        <span class="group-title">{{ t('sessions.groups.older') }}</span>
                    </div>
                    <div v-if="effectiveExpanded.older" class="session-group-content">
                        <div
                            v-for="session in groupedSessions.older"
                            :key="session.RecordId"
                            class="session-item"
                            :class="{
                                'session-item-active':
                                    sessionStore.currentSessionId === session.RecordId,
                            }"
                            @click="selectSession(session)"
                        >
                            <div class="session-content">
                                <div class="session-name">{{ session.Name }}</div>
                            </div>
                            <button
                                class="session-menu-button"
                                @click="showContextMenu($event, session.RecordId)"
                            >
                                ⋮
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="contextMenuSessionId"
            class="context-menu"
            :style="{ top: contextMenuPosition.y + 'px', left: contextMenuPosition.x + 'px' }"
            @click.stop
        >
            <div
                class="context-menu-item"
                @click="contextMenuSessionId && startRename(contextMenuSessionId)"
            >
                {{ t('sessions.menu.rename') }}
            </div>
            <div
                class="context-menu-item"
                @click="contextMenuSessionId && confirmDelete(contextMenuSessionId)"
            >
                {{ t('sessions.menu.delete') }}
            </div>
        </div>

        <div
            v-if="contextMenuSessionId"
            class="context-menu-backdrop"
            @click="closeContextMenu"
        ></div>

        <div v-if="showNewSessionPrompt" class="modal-overlay" @click="cancelNewSession">
            <div class="modal-content" @click.stop>
                <div class="modal-title">{{ t('sessions.createModal.title') }}</div>
                <input
                    v-model="newSessionName"
                    type="text"
                    :placeholder="t('sessions.createModal.placeholder')"
                    class="modal-input"
                    @keyup.enter="createNewSession"
                    @keyup.esc="cancelNewSession"
                />
                <div class="modal-buttons">
                    <button class="modal-button-cancel" @click="cancelNewSession">{{ t('common.cancel') }}</button>
                    <button class="modal-button-create" @click="createNewSession">{{ t('sessions.createModal.create') }}</button>
                </div>
            </div>
        </div>

        <div v-if="showDeleteConfirm" class="modal-overlay" @click="cancelDelete">
            <div class="modal-content" @click.stop>
                <div class="modal-title">{{ t('sessions.deleteModal.title') }}</div>
                <p>
                    {{ t('sessions.deleteModal.message') }}
                </p>
                <div class="modal-buttons">
                    <button
                        class="modal-button-cancel"
                        :disabled="deleting"
                        @click="cancelDelete"
                    >
                        {{ t('common.cancel') }}
                    </button>
                    <button
                        class="modal-button-delete"
                        :disabled="deleting"
                        @click="performDelete"
                    >
                        {{ t('common.delete') }}
                    </button>
                </div>
                <div v-if="deleting" class="modal-progress-bar" :aria-label="t('sessions.deleteModal.progress')"></div>
            </div>
        </div>

        <div v-if="showRenamePrompt" class="modal-overlay" @click="cancelRename">
            <div class="modal-content" @click.stop>
                <div class="modal-title">{{ t('sessions.renameModal.title') }}</div>
                <input
                    v-model="renameNewName"
                    type="text"
                    :placeholder="t('sessions.renameModal.placeholder')"
                    class="modal-input"
                    :disabled="renaming"
                    @keyup.enter="performRename"
                    @keyup.esc="cancelRename"
                />
                <div class="modal-buttons">
                    <button class="modal-button-cancel" :disabled="renaming" @click="cancelRename">
                        {{ t('common.cancel') }}
                    </button>
                    <button
                        class="modal-button-create"
                        :disabled="renaming"
                        @click="performRename"
                    >
                        {{ renaming ? t('sessions.renameModal.saving') : t('sessions.renameModal.rename') }}
                    </button>
                </div>
                <div v-if="renaming" class="modal-progress-bar" :aria-label="t('sessions.renameModal.progress')"></div>
            </div>
        </div>

        <div v-if="showToast" class="toast-notification">
            {{ toastMessage }}
        </div>
    </div>
</template>
